/** /language_policies/ — cross-tenant Language_Policy catalog (list + CRUD +
 *  used_by), plus the editor's lookups (predefined functions + selectable
 *  languages).
 *
 * A language policy is an ordered list of rules; each rule yields a language
 * code (or null) and the first non-null result wins. Rules are typed
 * (jsonpath / dotty / template / function / constant / block / nested
 * policy) and `definition` / `block_id` / `nested_policy_id` are interpreted
 * per type. `tour_operator` is nullable: a null tenant is a system-wide policy
 * usable by every operator.
 *
 * Hand-authored wrapper (console convention: never depend on generated types
 * for feature modules). `rules` is reconciled wholesale on write. No
 * optimistic-concurrency token — writes are last-writer-wins.
 */
import { runBatch, type BatchResponse, type BatchSelection } from './batch';
import { apiDelete, apiGet, apiPatch, apiPost } from './client';
import type { TenantRef } from './tours';
import type { PaginatedResponse } from './users';
import type { TenantIdsParam } from '@/scope/tenantIds';

/** Rule kinds, in display order. Each `type` selects the field it needs. */
export const RULE_TYPES = [
  { value: 'jsonpath', label: 'JSONPath' },
  { value: 'dotty', label: 'Dotty path' },
  { value: 'template', label: 'Template' },
  { value: 'function', label: 'Function' },
  { value: 'constant', label: 'Constant' },
  { value: 'block', label: 'Block' },
  { value: 'policy', label: 'Nested policy' },
] as const;
export type RuleType = (typeof RULE_TYPES)[number]['value'];

/** Template rendering engine — only meaningful for the `template` rule type. */
export const TEMPLATE_LANGUAGES = [
  { value: 'django', label: 'Django' },
  { value: 'jinja2', label: 'Jinja2' },
] as const;
export type TemplateLanguage = (typeof TEMPLATE_LANGUAGES)[number]['value'];

/** Lightweight block reference embedded in a `block` rule. */
export interface BlockRef {
  id: number;
  codename: string;
  name: string;
  type: string;
}

/** Lightweight (nested) policy reference embedded in a `policy` rule. */
export interface PolicyRef {
  id: number;
  codename: string;
  name: string;
}

/** One ordered rule (read shape). */
export interface Rule {
  id: number;
  order: number;
  type: RuleType;
  /** JSONPath / dotty path / template source / function name / constant
   *  language code (interpreted per `type`). */
  definition: string;
  /** Used by the `template` type. */
  template_language: TemplateLanguage;
  /** Used by the `block` type. */
  block: BlockRef | null;
  /** Used by the `policy` type. */
  nested_policy: PolicyRef | null;
}

/** One rule on write — the `type` selects which field is meaningful. */
export interface RuleWrite {
  type: RuleType;
  definition?: string;
  template_language?: TemplateLanguage;
  block_id?: number | null;
  nested_policy_id?: number | null;
  order?: number;
}

export interface Policy {
  id: number;
  /** Owning tenant. Null for system-wide policies. */
  tenant: TenantRef | null;
  codename: string;
  name: string;
  description: string;
  enabled: boolean;
  rule_count: number;
  created_ts: string | null;
  updated_ts: string | null;
}

export interface PolicyDetail extends Policy {
  /** Ordered rules. */
  rules: Rule[];
  /** Reference counts of the PROTECTed FKs that block a delete (409). */
  used_by: {
    notifications: number;
    policies: number;
  };
}

/** Create/update body. `codename` required on create; `tour_operator` null =
 *  system-wide. `rules`, when present, fully replaces the policy's rules
 *  (reconciled wholesale). Omit `rules` to leave them untouched. */
export interface PolicyWrite {
  tour_operator?: number | null;
  codename?: string;
  name?: string;
  description?: string;
  enabled?: boolean;
  rules?: RuleWrite[];
}

export interface ListParams {
  page?: number;
  pageSize?: number;
  ordering?: string;
  /** codename + name + description (icontains). */
  search?: string;
  /** One id or a comma list of ids (D1), or 'system' / 'null' for system-wide policies (no tenant). */
  tour_operator_id?: TenantIdsParam;
  enabled?: boolean;
}

function toQuery(params: ListParams): string {
  const usp = new URLSearchParams();
  if (params.page) usp.set('page', String(params.page));
  if (params.pageSize) usp.set('page_size', String(params.pageSize));
  if (params.ordering) usp.set('ordering', params.ordering);
  if (params.search) usp.set('search', params.search);
  if (params.tour_operator_id !== undefined) {
    usp.set('tour_operator_id', String(params.tour_operator_id));
  }
  if (params.enabled !== undefined) usp.set('enabled', String(params.enabled));
  const qs = usp.toString();
  return qs ? `?${qs}` : '';
}

/** List policies. Also the queryFn source for the `policy` rule autocomplete. */
export function list(params: ListParams = {}): Promise<PaginatedResponse<Policy>> {
  return apiGet<PaginatedResponse<Policy>>(`/language_policies/${toQuery(params)}`);
}

export function retrieve(id: number): Promise<PolicyDetail> {
  return apiGet<PolicyDetail>(`/language_policies/${id}/`);
}

export function create(body: PolicyWrite): Promise<PolicyDetail> {
  return apiPost<PolicyDetail>('/language_policies/', body);
}

export function update(id: number, body: PolicyWrite): Promise<PolicyDetail> {
  return apiPatch<PolicyDetail>(`/language_policies/${id}/`, body);
}

export function remove(id: number): Promise<void> {
  return apiDelete(`/language_policies/${id}/`);
}

/** Generic `/language_policies/batch/` delete dispatch (background task). */
export function batchDelete(
  selection: BatchSelection,
  filterQuery?: string,
): Promise<BatchResponse> {
  return runBatch('language_policies', 'delete', selection, undefined, filterQuery);
}

// ---- editor lookups -------------------------------------------------------

/** The registered predefined language-function names (empty → none available,
 *  so the editor greys out the `function` rule type). */
export function listLanguagePolicyFunctions(): Promise<string[]> {
  return apiGet<{ functions: string[] }>('/language_policy_functions/').then((r) => r.functions);
}

/** A selectable language for the constant-rule dropdown. */
export interface LanguageOption {
  code: string;
  name: string;
  /** True if enabled for the given tenant. */
  enabled: boolean;
}

/** Selectable languages — the tenant's enabled languages first (when a tenant
 *  id is given), then the full ISO 639-1 list. */
export function listLanguages(tourOperatorId?: number | null): Promise<LanguageOption[]> {
  const qs =
    tourOperatorId != null
      ? `?tour_operator_id=${encodeURIComponent(String(tourOperatorId))}`
      : '';
  return apiGet<{ results: LanguageOption[] }>(`/languages/${qs}`).then((r) => r.results);
}

// ---- test evaluation (visualized testing) ---------------------------------

/** Which entity id can supply a context for an event (gtu/order/user), or null
 *  when only pasted-JSON / delivery-log context is available. */
export type EntityKind = 'gtu' | 'order' | 'user' | null;

/** A supported event for the policy test tool. */
export interface SupportedEvent {
  code: string;
  family: string;
  /** Which entity id can supply a context (gtu/order/user), or null. */
  entity_kind: EntityKind;
}

/** Supported events for the test tool (GET events/). */
export function listPolicyEvents(): Promise<SupportedEvent[]> {
  return apiGet<{ events: SupportedEvent[] }>('/language_policies/events/').then((r) => r.events);
}

/** A test context, supplied one of three ways (mirrors the block test tool). */
export type EvaluateContext =
  | { mode: 'json'; json: string }
  | { mode: 'event_delivery_log'; event_delivery_log_id: string }
  | { mode: 'entity'; entity_id: number };

/** The (unsaved) policy under test — a snapshot of the current editor state. */
export interface PolicyUnderTest {
  tour_operator: number | null;
  rules: RuleWrite[];
}

export interface PolicyEvaluateRequest {
  /** Event code (from listPolicyEvents); required for entity context. */
  event: string;
  policy: PolicyUnderTest;
  context: EvaluateContext;
}

/** One per-rule row of the evaluation trace, in evaluation order. */
export interface RuleTrace {
  order: number;
  /** The rule's type, or null. */
  type: string | null;
  /** The rule's raw output (JSON-normalized) before language normalization. */
  raw: unknown;
  /** The normalized ISO 639-1 code this rule yielded, or null. */
  code: string | null;
  /** True for the first rule that yielded a code (the winner delivery stops at). */
  matched: boolean;
  /** Per-rule evaluation error, or null. */
  error: string | null;
}

export interface PolicyEvaluateResult {
  ok: boolean;
  /** The resolved language code (winning rule), or null (falls back to default). */
  result: string | null;
  trace: RuleTrace[];
  error: string | null;
  /** Top-level context namespaces available (excluding block). */
  context_keys: string[];
  fully_resolved?: boolean;
}

/** Evaluate an unsaved policy against a context (POST evaluate/). Never saves the
 *  policy. A 400 (bad JSON / missing id / unsupported entity family) is thrown as
 *  a ConsoleApiError so the caller can surface `.message` inline. */
export function evaluatePolicy(body: PolicyEvaluateRequest): Promise<PolicyEvaluateResult> {
  return apiPost<PolicyEvaluateResult>('/language_policies/evaluate/', body);
}
